/**
 * The two lines a picker draws over a list row (piece 3, the Dropdown). 🔨 The
 * mapping is declared once per record type here rather than at each picker, so
 * no picker can invent its own fallback and label a row with its identifier.
 * The keys read are the columns the list page's own register declares, so a
 * picker and the list beside it name a record the same way.
 */
import type { SuggestionRow } from '../controls/dropdownSearch';
import type { PagedRecord } from './recordPages';
import { postalAddressLine } from '../records/postalAddress';

/** A list row as the wire carries it: the declared columns, plus the id. */
export type ListRowValue = string | number | null | undefined;
export type SuggestionSource = Readonly<Record<string, ListRowValue>>;

/** What joins a person's title to the company they hold it at. */
export const POSITION_SEPARATOR = ' · ';

/** How many suggestions a picker offers before the Add New row. */
export const SUGGESTION_LIMIT = 20;

function text(value: ListRowValue): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

function joined(values: readonly ListRowValue[], separator: string): string {
  return values.map(text).filter(Boolean).join(separator);
}

/**
 * A person's position: the title their open employment carries and the company
 * they hold it at, as `Controls Engineer · Acme Corp`. A person with a title
 * and no employer reads as the title alone, and one employed nowhere reads
 * empty rather than falling back to anything else.
 */
export function personPosition(row: SuggestionSource): string {
  return joined([row.title, row.companyName], POSITION_SEPARATOR);
}

type Lines = (row: SuggestionSource) => { line1: string; line2: string };

/** One entry per record type a list page answers for, keyed the way its row is. */
const LINES: Readonly<Record<PagedRecord, Lines>> = {
  CompanyRecord: (row) => ({ line1: text(row.displayName), line2: '' }),
  PersonRecord: (row) => ({ line1: text(row.fullName), line2: personPosition(row) }),
  LocationRecord: (row) => ({ line1: text(row.addressLine), line2: postalAddressLine({ locality: text(row.locality), administrativeArea: text(row.administrativeArea), postalCode: text(row.postalCode) }) ?? '' }),
  SiteRecord: (row) => ({ line1: text(row.label), line2: text(row.companyName) }),
  Item: (row) => ({ line1: text(row.name), line2: text(row.sku) }),
  Deal: (row) => ({ line1: text(row.name), line2: text(row.customerName) }),
  Lead: (row) => ({ line1: text(row.name), line2: text(row.companyName) }),
  PurchaseOrder: (row) => ({ line1: text(row.purchaseOrderNumber), line2: text(row.vendorName) }),
  Quote: (row) => ({ line1: text(row.number), line2: text(row.companyName) }),
  WorkOrder: (row) => ({ line1: text(row.workOrderNumber), line2: text(row.customerName) }),
  ServiceReport: (row) => ({ line1: text(row.number), line2: text(row.customerName) }),
  SalesOrder: (row) => ({ line1: text(row.salesOrderNumber), line2: text(row.companyName) }),
  Invoice: (row) => ({ line1: text(row.number), line2: text(row.companyName) }),
  CreditNote: (row) => ({ line1: text(row.creditNoteNumber), line2: text(row.companyName) }),
  Payment: (row) => ({ line1: text(row.reference), line2: text(row.companyName) }),
  RFQ: (row) => ({ line1: text(row.rfqNumber), line2: text(row.dealName) }),
  // 🔒 A list names itself first and its Deals second (decision 1092).
  BOM: (row) => ({ line1: text(row.name), line2: text(row.dealName) }),
  Bill: (row) => ({ line1: text(row.billNumber), line2: text(row.vendorName) }),
  VendorCredit: (row) => ({ line1: text(row.creditNumber), line2: text(row.vendorName) }),
  StockCount: (row) => ({ line1: text(row.countNumber), line2: text(row.siteName) }),
  StockTransfer: (row) => ({ line1: text(row.transferNumber), line2: joined([row.fromSiteName, row.toSiteName], ' → ') }),
  StockRevaluation: (row) => ({ line1: text(row.revaluationNumber), line2: text(row.sku) }),
  JournalEntry: (row) => ({ line1: text(row.entryNumber), line2: text(row.memo) }),
};

/**
 * One list row as a suggestion, or null where the row carries no name. 🔨 A
 * row is dropped rather than labelled with its identifier: the product owner
 * met the Person picker listing raw uuids (2026-09-15), and a uuid names
 * nobody. Dropping the row leaves the empty-state row and Add New standing,
 * which is a picker that says it found nothing rather than one that lies.
 */
export function suggestionRowFor(recordType: PagedRecord, row: SuggestionSource): SuggestionRow | null {
  const id = text(row.id);
  if (!id) return null;
  const { line1, line2 } = LINES[recordType](row);
  if (!line1) return null;
  return line2 ? { id, line1, line2 } : { id, line1 };
}

/** The rows a picker offers, in the order the list answered, with the nameless dropped. */
export function suggestionRowsFor(recordType: PagedRecord, rows: readonly SuggestionSource[], limit = SUGGESTION_LIMIT): SuggestionRow[] {
  const shown: SuggestionRow[] = [];
  for (const row of rows) {
    const suggestion = suggestionRowFor(recordType, row);
    if (suggestion) shown.push(suggestion);
    if (shown.length === limit) break;
  }
  return shown;
}
